import type { Ref, ShallowRef } from "vue";
import type { VideoJsPlayer as IPixellotPlayer } from "@pixellot/web-sdk";
import type { IPlayerState } from "../util/state";
import type { IPixellotPlayerSource } from "../types";
import { ref } from "vue";

export default function useSource(
  videoJSPlayer: ShallowRef<IPixellotPlayer | null>,
  state: Ref<IPlayerState | null>
) {
  const source = ref<IPixellotPlayerSource>({
    active: "hd" as "hd" | "pano",
    hd: null,
    hdThumbnails: null,
    pano: null,
    panoThumbnails: null,
    hdAds: null,
    panoAds: null,
  });

  function setSrc(src: string | IPixellotPlayerSource) {
    const player = videoJSPlayer.value!;
    const hasSrc = Boolean(player.src());
    const playedTime = player.currentTime();
    const wasPlaying = !player.paused();
    const playedVolume = player.volume();

    if (hasSrc && player.unload) {
      player.unload({ loading: true });
    }

    const nextSource = getSrc(source.value, src);
    const nextSrcString = nextSource[
      nextSource.active as keyof IPixellotPlayerSource
    ] as string | null;

    source.value = nextSource;

    if (nextSrcString) {
      player.src(nextSrcString || "");
    } else {
      const oppositeMode = nextSource.active === "hd" ? "pano" : "hd";

      if (nextSource[oppositeMode]) {
        source.value.active = oppositeMode;
        player.src(nextSource[oppositeMode] || "");
      } else {
        player.src(nextSrcString || "");
      }
    }

    player.volume(playedVolume);

    const thumbnailsSrc = nextSource[`${nextSource.active || "hd"}Thumbnails`];

    if (thumbnailsSrc) {
      const thumb = parseThumbnailUrl(thumbnailsSrc);

      if (thumb) {
        const options = {
          url: thumb?.url,
          width: thumb?.width,
          height: thumb?.height,
          interval: Number(thumb?.interval),
          duration: Number(thumb?.duration),
          rows: Number(thumb?.rows),
          cells: Number(thumb?.cells),
        };

        player.spriteThumbnails().updateOptions(options);
      }
        
    }

    // TODO: ...
    const isLive = state.value?.isLive;

    if (typeof src === "object" && src.isSwitching) {
      player.one("loadedmetadata", () => {
        if (isLive) {
          return;
        }

        if (player.currentTime() !== playedTime) {
          player.currentTime(playedTime);
        }

        if (wasPlaying && player.paused()) {
          const promise = player.play();

          if (promise !== undefined) {
            promise.catch((error) => {
              // 90% AbortError, means that play() was aborted by the browser.
              // Easy reproducible on iOS devices.
              console.warn(error);
            });
          }
        }

        player.panoAPI.reset();
      });
    }

    return source.value;
  }

  return { source, setSrc };
}

function getSrc(
  currentSrc: IPixellotPlayerSource,
  nextSrc: string | IPixellotPlayerSource
): IPixellotPlayerSource {
  if (typeof nextSrc === "string")
    return { ...currentSrc, active: "hd", hd: nextSrc };
  else if (typeof nextSrc === "object")
    return {
      active: nextSrc.active === undefined ? currentSrc.active : nextSrc.active,
      hd: nextSrc.hd === undefined ? currentSrc.hd : nextSrc.hd,
      hdThumbnails:
        nextSrc.hdThumbnails === undefined
          ? currentSrc.hdThumbnails
          : nextSrc.hdThumbnails,
      pano: nextSrc.pano === undefined ? currentSrc.pano : nextSrc.pano,
      panoThumbnails:
        nextSrc.panoThumbnails === undefined
          ? currentSrc.panoThumbnails
          : nextSrc.panoThumbnails,
      tactical: nextSrc.tactical === undefined ? currentSrc.tactical : nextSrc.tactical,
      tacticalThumbnails:
        nextSrc.tacticalThumbnails === undefined
          ? currentSrc.tacticalThumbnails
          : nextSrc.tacticalThumbnails,
      hdAds: nextSrc.hdAds === undefined ? currentSrc.hdAds : nextSrc.hdAds,
      panoAds: nextSrc.panoAds === undefined ? currentSrc.panoAds : nextSrc.panoAds,
      tacticalAds: nextSrc.tacticalAds === undefined ? currentSrc.tacticalAds : nextSrc.tacticalAds
    };
  return currentSrc;
}

export interface ThumbnailsSprite {
  url: string; // thumbnail sprite's url
  start: number; // start timestamp of this sprite in video
  duration: number; // duration of this sprite in video
  width: number; // width of each preview image
  height: number; // height of each preview image
  interval: number; // interval of each preview images in sprite

  // Other
  cells: number;
  rows: number;
}

const parseObjectFromString = (str: string) => {
  const pattern = new RegExp("([^?=&]+)(=([^&]*))?", "g");
  const matches = str.match(pattern) || [] as string[];
  
  return matches.reduce((acc: Record<string, any>, item: string) => {
    const [key, value] = item.split("=");

    acc[key] = value;

    return acc;
  }, {});
};

function parseThumbnailUrl(thumbnailUrl: string): null | ThumbnailsSprite {
  const [url, config] = thumbnailUrl.split("#");

  if (!url || !config) {
    return null;
  }

  const { d: interval, wh, grid } = parseObjectFromString(config);
  const [width, height] = wh.split(",");
  const [cells, rows] = grid.split(",");

  return {
    start: 0,
    url,
    width,
    height,
    duration: cells * rows * interval,
    interval,
    // #
    cells,
    rows,
  };
}
